import { useRef } from "react";
import { useDispatch } from "react-redux";
import { matchPath } from "react-router";
import { fileAudioIcon, fileDataIcon, fileIcon, fileImageIcon, filePdfIcon, filePptIcon, fileTxtIcon, folderIcon } from "@progress/kendo-svg-icons";
import { format } from "date-fns";
import { setLibraryAttachment } from "../../actions/common";
import {
  setContentManagerData,
  setContentManagerDataDetails,
  setContentManagerDataView,
  setFileMetaData,
  setSelectedContentItem,
} from "../../actions/contentManager";
import { dateFormat, label, notifyIcon, number, quote, route } from "../../config";
import { appendFieldsFromObject, createFormData } from "../../helper/formDataHelper";
import {
  addContentAttachments,
  getContentAttachmentById,
  getContentAttachments,
  getDeletedContentAttachments,
  getQuickAccessContentAttachments,
  getRecentContentAttachments,
  getTaskContentAttachments,
  updateActionOnContentAttachment,
  updateContentAttachment,
} from "../../shared/services/contentManger.services";
import store from "../../store";
import { getNotification } from "../../utils/common";
import { getBookmarks } from "../Bookmarks/bookmarks.service";
import { downloadFile } from "../TaskSidebar/TaskSidebarContent/Attachments/attachments.helper";
import { convertToTimeZone } from "../../helper/common";

/**
 * Converts a file extension to an icon and type.
 *
 * @param {string} item - The file name or path.
 * @returns {Object|null} An object containing the icon and type, or null if no item is provided.
 */
export const convertExtensionToIcon = (item) => {
  if (!item.name) {
    return null;
  }
  const extension = item.type === label.FOLDER ? null : item.name.split(".").pop();

  switch (extension ? extension.toLowerCase() : null) {
    case "pdf":
      return {
        svgIcon: filePdfIcon,
        type: "Data",
      };
    case "ppt":
    case "pptx":
      return {
        svgIcon: filePptIcon,
        type: "Data",
      };
    case "xlsx":
    case "xls":
      return {
        svgIcon: fileDataIcon,
        type: "Data",
      };
    case "jpg":
    case "png":
      return {
        svgIcon: fileImageIcon,
        type: "Image",
      };
    case "txt":
    case "doc":
    case "docx":
      return {
        svgIcon: fileTxtIcon,
        type: "Text",
      };
    case "mp3":
    case "mp4":
    case "mp":
      return {
        svgIcon: fileAudioIcon,
        type: "Text",
      };
    case "dox":
      return {
        svgIcon: doxSvgIcon,
        type: "Text",
      };
    case null:
      return {
        svgIcon: folderIcon,
        type: label.FOLDER,
      };
    default:
      return {
        svgIcon: fileIcon,
        type: label.FOLDER,
      };
  }
};

/**
 * Extracts the name of a file or folder from a given path.
 *
 * @param {string} path - The file or folder path.
 * @returns {string} The name of the file or folder.
 */
export const getName = (path) => {
  if (!path) {
    return path;
  }
  return path.split("/").pop();
};

/**
 * Retrieves the folder path from a given file path.
 *
 * @param {string} path - The file path.
 * @returns {string} The folder path.
 */
export const getFolderPath = (path) => {
  if (!path) {
    return path;
  }
  const pathArr = path.split("/");
  pathArr.pop();
  return pathArr.join("/");
};

/**
 * Formats a date in the user's timezone (from Redux) for display.
 * Use for CMS date display so it updates when user timezone changes.
 *
 * @param {Date|string} date - The date (UTC) to format.
 * @returns {string} The formatted date in user timezone.
 */
export const convertDateFormat = (date) => {
  return date ? convertToTimeZone(date, dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) : date;
};

const resolveParentId = (item) => {
  if (item.workflowId || item.id === label.TASK_ASSETS_ROOT) return label.TASK_ASSETS_ROOT;
  if (item.isQuickAccess || item.id === label.QUICK_ACCESS_ROOT) return label.QUICK_ACCESS_ROOT;
  return item.parentId || label.PROJECT_ROOT;
};

/**
 * Recursively maps and formats data items.
 *
 * @param {Array<Object>} data - The data items to map.
 * @returns {Array<Object>} The mapped and formatted data items.
 */
export const mapData = (data, parentPath, inheritedProjectId = null) => {
  if (!data) {
    return [];
  }

  return data.map((item) => {
    const sanitizedName = sanitizeName(item.name, item.type === label.FILE);
    const currentPath = parentPath ? `${parentPath}/${sanitizedName}` : sanitizedName;

    return {
      id: item.attachmentId || item.id,
      type: item.type,
      name: sanitizedName,
      path: currentPath,
      dateCreated: item.createdAt ?? item.dateCreated,
      dateModified: item.updatedAt ?? item.dateModified,
      size: item.type === label.FILE ? item.size : "",
      icon: convertExtensionToIcon(item),
      items: item.items && item.items.length ? mapData(item.items, currentPath, item.projectId ?? inheritedProjectId) : [],
      hasChildren: item.hasChildren,
      expanded: item.expanded || false,
      createdBy: item.createdBy,
      modifiedBy: item.modifiedBy ?? item.createdBy,
      disabled: item.disabled || false,
      attachmentKey: item.type === label.FILE ? item.attachmentKey : "",
      folderTreeIcon: item.folderTreeIcon || null,
      isDeleted: item.isDeleted,
      parentPath: parentPath,
      parentId: resolveParentId(item),
      link: item.link || null,
      mimeType: item.mimeType || null,
      isFavorite: item.isFavorite,
      tagIds: item.tagIds,
      hasReminder: item.hasReminder || false,
      projectId: item.projectId ?? inheritedProjectId,
      workflowId: item.workflowId || null,
      quickAccessFolderId: item.quickAccessFolderId || null,
      taskId: item.taskId || null,
      taskDetail: item.taskDetail || null,
    };
  });
};

/**
 * helper function to create root folder objects
 */
const createRootFolder = (id, name, path, folderTreeIcon, options = {}) => ({
  id,
  type: label.FOLDER,
  name,
  path,
  items: [],
  hasChildren: false,
  disabled: true,
  folderTreeIcon,
  ...options,
});

/**
 * Constructs the respective projects structure, including root folders and mapped state data.
 *
 * @param {Array<Object>} projectData
 *  * @param {Array<Object>} trashData
 * @returns {Array<Object>} - The constructed Project Documents folder structure.
 */
export const constructProjectLibrary = (
  projectData = [],
  trashData = [],
  taskAssetsData = [],
  recentData = [],
  projectName,
  projectId,
  allProjects = [],
  isLibraryView = false,
  quickAccessFolderData = null
) => {
  const documentsName = projectName ? `${label.LIBRARY}` : `${label.MY_LIBRARY}`;
  const documentsPath = `/${documentsName}`;

  const documentsRoot = createRootFolder(label.DOCUMENTS_ROOT, documentsName, documentsPath, "FOLDER", {
    disabled: false,
    projectId: projectId,
  });

  const projectRoot = createRootFolder(
    label.PROJECT_ROOT,
    label.FILES_AND_FOLDER_DOCUMENTS,
    `${documentsPath}/${label.FILES_AND_FOLDER_DOCUMENTS}`,
    "FOLDER",
    {
      disabled: false,
      projectId: projectId,
    }
  );

  const taskAssetsRoot = createRootFolder(label.TASK_ASSETS_ROOT, label.TASK_ASSETS, `${documentsPath}/${label.TASK_ASSETS}`, "FOLDER", {
    disabled: false,
    projectId: projectId,
  });

  const recentRoot = createRootFolder(label.RECENT_ROOT, label.RECENT, `${documentsPath}/${label.RECENT}`, "RECENT_CONTENT", {
    disabled: false,
    projectId: projectId,
  });

  const sharedWithMeRoot = createRootFolder(label.SHARED_ROOT, label.SHARED_WITH_ME, `${documentsPath}/${label.SHARED_WITH_ME}`, "SHARED_CONTENT", {
    disabled: true,
  });

  const quickAccessName =   quickAccessFolderData?.name || label.VAULT;
  const quickAccessRoot = createRootFolder(label.QUICK_ACCESS_ROOT, quickAccessName, `${documentsPath}/${quickAccessName}`, "FOLDER", {
    disabled: false,
    projectId: projectId,
    quickAccessFolderId: quickAccessFolderData?._id || null,
  });

  const trashRoot = createRootFolder(label.TRASH_ROOT, label.TRASH, `${documentsPath}/${label.TRASH}`, "DELETE_ICON", {
    disabled: false,
    projectId: projectId,
  });

  if (isLibraryView && allProjects && allProjects.length > 0) {
    const myDocumentsContent = mapData(projectData, `${documentsPath}/${label.FILES_AND_FOLDER_DOCUMENTS}`);
    const myDocumentsTrashContent = mapData(trashData, `${documentsPath}/${label.TRASH}`);
    const myDocumentsTaskAssetsContent = mapData(taskAssetsData, `${documentsPath}/${label.TASK_ASSETS}`);
    const myDocumentsRecentContent = mapData(recentData, `${documentsPath}/${label.RECENT}`);

    const myDocumentsItems = [
      {
        ...projectRoot,
        items: myDocumentsContent,
        hasChildren: myDocumentsContent.some((data) => data.type === label.FOLDER),
      },
      { ...quickAccessRoot },
      {
        ...taskAssetsRoot,
        items: myDocumentsTaskAssetsContent,
        hasChildren: myDocumentsTaskAssetsContent.some((data) => data.type === label.FOLDER),
      },
      { ...recentRoot, items: myDocumentsRecentContent },
      sharedWithMeRoot,
      {
        ...trashRoot,
        items: myDocumentsTrashContent,
      },
    ];

    const myDocuments = {
      ...documentsRoot,
      items: myDocumentsItems,
      hasChildren: true,
      expanded: true,
    };

    const projectEntries = allProjects.map((project) => {
      const projectId = project.ProjectId;
      const projectName = project.ProjectName || project.Name || project.DisplayName;
      const projectPath = `${documentsPath}/${projectName}`;

      return createRootFolder(`project_${projectId}`, projectName, projectPath, "FOLDER", {
        disabled: false,
        projectId: projectId,
        expanded: false,
        hasChildren: true,
        items: [],
      });
    });

    return [myDocuments, ...projectEntries];
  } else {
    const projectContent = mapData(projectData, `${documentsPath}/${label.FILES_AND_FOLDER_DOCUMENTS}`);
    const trashContent = mapData(trashData, `${documentsPath}/${label.TRASH}`);
    const taskAssetsContent = mapData(taskAssetsData, `${documentsPath}/${label.TASK_ASSETS}`);
    const recentContent = mapData(recentData, `${documentsPath}/${label.RECENT}`);

    const documentsItems = [
      {
        ...projectRoot,
        items: projectContent,
        hasChildren: projectContent.some((data) => data.type === label.FOLDER),
      },
      { ...taskAssetsRoot, items: taskAssetsContent, hasChildren: taskAssetsContent.some((data) => data.type === label.FOLDER) },
      { ...recentRoot, items: recentContent },
      sharedWithMeRoot,
      {
        ...trashRoot,
        items: trashContent,
      },
    ];

    return [
      {
        ...documentsRoot,
        items: documentsItems,
        hasChildren: true,
      },
    ];
  }
};

/**
 * Formats data using the `mapData` function.
 *
 * @param {Array<Object>} data - The data to format.
 * @returns {Array<Object>} The formatted data.
 */
export const formatData = (data, projectName) => {
  return mapData(data, projectName);
};

/**
 * Recursively constructs a folder tree from data.
 *
 * @param {Array<Object>} data - The data to process.
 * @param {Object} selectedItem - The selected folder or file.
 * @returns {Array<Object>} The folder tree.
 */
export const getFolderTree = (data, selectedItem = null) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }
  const newItems = [];

  for (let index = 0; index < data.length; index++) {
    const currentItem = { ...data[index] };
    if (currentItem.path && currentItem.type === label.FOLDER) {
      if (currentItem.items && currentItem.items.length) {
        // For Recent and Trash folders, don't show their children in tree view
        if (
          currentItem.id === label.RECENT_ROOT ||
          currentItem.id?.startsWith(`${label.RECENT_ROOT}_`) ||
          currentItem.id === label.TRASH_ROOT ||
          currentItem.id?.startsWith(`${label.TRASH_ROOT}_`)
        ) {
          // Keep the Recent/Trash folder but remove its children from tree view
          newItems.push({
            ...currentItem,
            items: [], // Remove children from tree view
            hasChildren: false, // Mark as no children for tree view
            selected: selectedItem ? selectedItem.path === currentItem.path : false,
          });
        } else {
          // For other folders, process children normally
          currentItem.items = getFolderTree(currentItem.items, selectedItem);
          newItems.push({
            ...currentItem,
            selected: selectedItem ? selectedItem.path === currentItem.path : false,
          });
        }
      } else {
        newItems.push({
          ...currentItem,
          selected: selectedItem ? selectedItem.path === currentItem.path : false,
        });
      }
    }
  }
  return newItems;
};

/**
 * Searches for a specific item in a tree structure.
 *
 * @param {Array<Object>} data - The data tree to search.
 * @param {Object} selectedItem - The item to find.
 * @returns {Object|null} The found item or null.
 */
export const searchTreeItem = (data, selectedItem) => {
  if (!selectedItem) {
    return data;
  }

  for (let index = 0; index < data.length; index++) {
    const currentItem = data[index];
    if (selectedItem.path ? currentItem.path === selectedItem.path : currentItem.id === selectedItem.id) {
      return currentItem;
    }
    if (currentItem.items && currentItem.items.length) {
      const foundItem = searchTreeItem(currentItem.items, selectedItem);
      if (foundItem) {
        return foundItem;
      }
    }
  }
};

/**
 * Adds data items to a specified location in the tree.
 * @returns {Array<Object>} The updated data.
 */
export const addDataItem = async (data, selectedItem = null, files, projectId) => {
  const state = store.getState();
  const { user } = state.auth;

  if (selectedItem.id === label.QUICK_ACCESS_ROOT) {
    const payload = {
      parentId: selectedItem.quickAccessFolderId ?? null,
      type: label.FILE,
      files: files.map((file) => file.getRawFile()),
    };
    const formData = createFormData();
    appendFieldsFromObject(formData, payload);
    const addedAttachments = await addContentAttachments({
      payload: formData,
      userId: user.id,
      companyId: user.companyId,
      projectId: projectId,
    });
    if (addedAttachments) {
      selectedItem.items = selectedItem.items || [];
      const newFiles = addedAttachments.map((attachment, index) => {
        const file = files[index];
        return {
          id: attachment.attachmentId,
          path: `${selectedItem.path}/${file.name}`,
          dateCreated: attachment.createdAt,
          dateModified: convertToTimeZone(new Date(), dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) || new Date(),
          size: file.size,
          name: attachment.name,
          type: attachment.type,
          icon: convertExtensionToIcon(file),
          items: [],
          hasChildren: attachment.hasChildren,
          createdBy: user.id,
          attachmentKey: attachment.attachmentKey,
          parentId: label.QUICK_ACCESS_ROOT,
          mimeType: attachment.mimeType,
          link: attachment.link,
          parentPath: selectedItem.path,
          tagIds: attachment.tagIds,
        };
      });

      selectedItem.items.push(...newFiles);
    }

    return data;
  }

  if (selectedItem.id === label.PROJECT_ROOT || selectedItem.id.startsWith(`${label.PROJECT_ROOT}_`)) {
    let actualProjectId = projectId;
    if (selectedItem.id.startsWith(`${label.PROJECT_ROOT}_`)) {
      const extractedProjectId = selectedItem.id.split("_")[1];
      actualProjectId = extractedProjectId ? parseInt(extractedProjectId) : projectId;
    }

    const payload = {
      type: label.FILE,
      files: files.map((file) => file.getRawFile()),
    };
    const formData = createFormData();
    appendFieldsFromObject(formData, payload);
    const addedAttachments = await addContentAttachments({
      payload: formData,
      userId: user.id,
      companyId: user.companyId,
      projectId: actualProjectId,
    });

    if (addedAttachments) {
      selectedItem.items = selectedItem.items || [];
      const newFiles = addedAttachments.map((attachment, index) => {
        const file = files[index];
        return {
          id: attachment.attachmentId,
          path: `${selectedItem.path}/${file.name}`,
          dateCreated: attachment.createdAt,
          dateModified: convertToTimeZone(new Date(), dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) || new Date(),
          size: file.size,
          name: attachment.name,
          type: attachment.type,
          icon: convertExtensionToIcon(file),
          items: [],
          hasChildren: attachment.hasChildren,
          createdBy: user.id,
          attachmentKey: attachment.attachmentKey,
          parentId: attachment.parentId,
          mimeType: attachment.mimeType,
          link: attachment.link,
          parentPath: selectedItem.path,
          tagIds: attachment.tagIds,
        };
      });

      selectedItem.items.push(...newFiles);
    }

    return data;
  }

  if (!data) {
    return data;
  }

  return await Promise.all(
    data.map(async (item) => {
      const currentItem = { ...item };

      if (currentItem.path === selectedItem.path) {
        if (!currentItem.items) {
          currentItem.items = [];
        }

        const payload = {
          parentId: selectedItem.id,
          type: label.FILE,
          files: files.map((file) => file.getRawFile()),
        };

        const formData = createFormData();
        appendFieldsFromObject(formData, payload);

        const addedAttachments = await addContentAttachments({
          payload: formData,
          userId: user.id,
          companyId: user.companyId,
          projectId: selectedItem?.projectId ?? projectId,
        });

        if (addedAttachments) {
          const newFiles = addedAttachments.map((attachment, index) => {
            const file = files[index];
            return {
              id: attachment.attachmentId,
              parentId: selectedItem.id,
              type: attachment.type,
              path: `${currentItem.path}/${file.name}`,
              name: attachment.name,
              dateCreated: attachment.createdAt,
              dateModified: convertToTimeZone(new Date(), dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) || new Date(),
              size: file.size,
              icon: convertExtensionToIcon(file),
              items: [],
              hasChildren: attachment.hasChildren,
              createdBy: user.id,
              attachmentKey: attachment.attachmentKey,
              parentId: attachment.parentId,
              mimeType: attachment.mimeType,
              link: attachment.link,
              parentPath: selectedItem.path,
              tagIds: attachment.tagIds,
            };
          });

          currentItem.items.push(...newFiles);
        }
      } else if (currentItem.items && currentItem.items.length) {
        currentItem.items = await addDataItem(currentItem.items, selectedItem, files, projectId);
      }

      return currentItem;
    })
  );
};

/**
 * Adds a new folder to the data tree at a specified location.
 * @returns {Array<Object>} The updated data.
 */
export const addFolder = async (data, selectedItem, folderCount, projectId, customFolderName) => {
  const state = store.getState();
  const { user } = state.auth;

  if (selectedItem.id === label.PROJECT_ROOT || selectedItem.id === label.DOCUMENTS_ROOT || selectedItem.id.startsWith(`${label.PROJECT_ROOT}_`)) {
    const folderName = customFolderName || (folderCount ? `${label.NEW_FOLDER} (${folderCount})` : label.NEW_FOLDER);

    let actualProjectId = projectId;
    if (selectedItem.id.startsWith(`${label.PROJECT_ROOT}_`)) {
      const extractedProjectId = selectedItem.id.split("_")[1];
      actualProjectId = extractedProjectId ? parseInt(extractedProjectId) : projectId;
    }

    const payload = {
      parentId: null,
      type: label.FOLDER,
      path: `${selectedItem.path}/${folderName}`,
      folderName: folderName,
    };

    let addedAttachment = await addContentAttachments({
      payload,
      userId: user.id,
      companyId: user.companyId,
      projectId: actualProjectId,
    });

    if (addedAttachment && addedAttachment.length) {
      const newFolder = {
        id: addedAttachment[0].attachmentId,
        parentId: null,
        path: `${selectedItem.path}/${folderName}`,
        name: addedAttachment[0].name,
        type: label.FOLDER,
        dateCreated: addedAttachment[0].createdAt,
        dateModified: convertToTimeZone(new Date(), dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) || new Date(),
        icon: convertExtensionToIcon(addedAttachment[0]),
        items: [],
        hasChildren: addedAttachment[0].hasChildren,
        expanded: false,
        createdBy: user.id,
        parentId: addedAttachment[0].parentId,
        parentPath: selectedItem.path,
        tagIds: addedAttachment[0].tagIds,
      };
      selectedItem.items.push(newFolder);
      if (!selectedItem.hasChildren) selectedItem.hasChildren = true;
    }

    return data;
  }

  if (!data) {
    return data;
  }

  const updatedData = await Promise.all(
    data.map(async (item) => {
      const currentItem = { ...item };

      if (currentItem.path === selectedItem?.path) {
        const folderName = customFolderName || (folderCount ? `${label.NEW_FOLDER} (${folderCount})` : label.NEW_FOLDER);

        if (!currentItem.items) {
          currentItem.items = [];
        }
        const payload = {
          parentId: selectedItem.id,
          type: label.FOLDER,
          folderName: folderName,
          size: number.ONE_THOUSAND_TWENTY_FOUR,
          path: `${currentItem.path}/${folderName}`,
        };

        let addedAttachment = await addContentAttachments({
          payload,
          userId: user.id,
          companyId: user.companyId,
          projectId: selectedItem?.projectId ?? projectId,
        });

        if (addedAttachment && addedAttachment.length) {
          const newFolder = {
            id: addedAttachment[0].attachmentId,
            parentId: selectedItem.id,
            name: addedAttachment[0].name,
            type: label.FOLDER,
            path: `${currentItem.path}/${folderName}`,
            dateCreated: addedAttachment[0].createdAt,
            dateModified: convertToTimeZone(new Date(), dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) || new Date(),
            icon: convertExtensionToIcon(addedAttachment[0]),
            items: [],
            hasChildren: addedAttachment[0].hasChildren,
            parentId: addedAttachment[0].parentId,
            expanded: false,
            createdBy: user.id,
            parentPath: selectedItem.path,
            tagIds: addedAttachment[0].tagIds,
          };

          currentItem.items.push(newFolder);
          if (!currentItem.hasChildren) currentItem.hasChildren = true;
        }
      }
      if (currentItem.items && currentItem.items.length) {
        currentItem.items = await addFolder(currentItem.items, selectedItem, folderCount, projectId, customFolderName);
      }

      return currentItem;
    })
  );

  return updatedData;
};

// Function to get a unique filename
const getUniqueFileName = (existingItems) => {
  let baseName = "untitled";
  let extension = ".dox";
  let newFileName = baseName + extension;
  let counter = 1;

  const existingNames = new Set(existingItems.map((item) => item.name));

  while (existingNames.has(newFileName)) {
    newFileName = `${baseName}${counter}${extension}`;
    counter++;
  }

  return newFileName;
};

/**
 * Adds a new folder to the data tree at a specified location.
 * @returns {Array<Object>} The updated data.
 */
export const addYDoc = async (data, selectedItem, folderCount, projectId, customDoxName) => {
  const state = store.getState();
  const { user } = state.auth;
  let newYDocId = null;

  const ensureDoxExtension = (name) => {
    if (name && !name.toLowerCase().endsWith(label.DOX_EXT)) {
      return `${name}${label.DOX_EXT}`;
    }
    return name;
  };

  if (selectedItem && selectedItem.id === label.QUICK_ACCESS_ROOT) {
    const yDocName = ensureDoxExtension(customDoxName) || label.DOX_DEFAULT_NAME;

    const payload = {
      parentId: selectedItem.quickAccessFolderId ?? null,
      type: "file",
      path: `${selectedItem.path}/${yDocName}`,
      name: yDocName,
      mimeType: label.DOX_MIME_TYPE,
    };
    let addedAttachment = await addContentAttachments({
      payload,
      userId: user.id,
      companyId: user.companyId,
      projectId: projectId,
    });
    if (addedAttachment && addedAttachment.length) {
      const newYDoc = {
        id: addedAttachment[0].attachmentId,
        parentId: label.QUICK_ACCESS_ROOT,
        path: `${selectedItem.path}/${yDocName}`,
        name: addedAttachment[0].name,
        type: "file",
        mimeType: label.DOX_MIME_TYPE,
        dateCreated: convertToTimeZone(new Date(), dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) || new Date(),
        dateModified: convertToTimeZone(new Date(), dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) || new Date(),
        items: [],
        hasChildren: addedAttachment[0].hasChildren,
        expanded: false,
        createdBy: user.id,
        icon: convertExtensionToIcon(addedAttachment[0]),
      };
      newYDocId = newYDoc.id;
      selectedItem.items = selectedItem.items || [];
      selectedItem.items.push(newYDoc);
      if (!selectedItem.hasChildren) selectedItem.hasChildren = true;
    }
    return { updatedData: data, newYDocId };
  }

  if (
    selectedItem &&
    (selectedItem.id === label.PROJECT_ROOT || selectedItem.id === label.DOCUMENTS_ROOT || selectedItem.id.startsWith(`${label.PROJECT_ROOT}_`))
  ) {
    const yDocName = ensureDoxExtension(customDoxName) || label.DOX_DEFAULT_NAME;

    let actualProjectId = projectId;
    if (selectedItem.id.startsWith(`${label.PROJECT_ROOT}_`)) {
      const extractedProjectId = selectedItem.id.split("_")[1];
      actualProjectId = extractedProjectId ? parseInt(extractedProjectId) : projectId;
    }

    const payload = {
      parentId: null,
      type: "file",
      path: `${selectedItem.path}/${yDocName}`,
      name: yDocName,
      mimeType: label.DOX_MIME_TYPE,
    };
    let addedAttachment = await addContentAttachments({
      payload,
      userId: user.id,
      companyId: user.companyId,
      projectId: actualProjectId,
    });
    if (addedAttachment && addedAttachment.length) {
      const newYDoc = {
        id: addedAttachment[0].attachmentId,
        parentId: null,
        path: `${selectedItem.path}/${yDocName}`,
        name: addedAttachment[0].name,
        type: "file",
        mimeType: label.DOX_MIME_TYPE,
        dateCreated: addedAttachment[0].createdAt,
        dateModified: convertToTimeZone(new Date(), dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) || new Date(),
        items: [],
        hasChildren: addedAttachment[0].hasChildren,
        expanded: false,
        createdBy: user.id,
        icon: convertExtensionToIcon(addedAttachment[0]),
      };
      newYDocId = newYDoc.id;
      selectedItem.items.push(newYDoc);
      if (!selectedItem.hasChildren) selectedItem.hasChildren = true;
    }
    return { updatedData: data, newYDocId };
  }

  if (!data) {
    return { updatedData: data, newYDocId };
  }
  const updatedData = await Promise.all(
    data.map(async (item) => {
      const currentItem = { ...item };

      if (selectedItem && currentItem.path === selectedItem.path) {
        const yDocName = ensureDoxExtension(customDoxName) || label.DOX_DEFAULT_NAME;

        if (!currentItem.items) {
          currentItem.items = [];
        }
        const payload = {
          parentId: selectedItem.id,
          type: "file",
          path: `${currentItem.path}/${yDocName}`,
          name: yDocName,
          mimeType: label.DOX_MIME_TYPE,
        };

        let addedAttachment = await addContentAttachments({
          payload,
          userId: user.id,
          companyId: user.companyId,
          projectId: selectedItem?.projectId ?? projectId,
        });

        if (addedAttachment && addedAttachment.length) {
          const newYDoc = {
            id: addedAttachment[0].attachmentId,
            parentId: selectedItem.id,
            name: addedAttachment[0].name,
            type: "file",
            mimeType: label.DOX_MIME_TYPE,
            path: `${currentItem.path}/${yDocName}`,
            dateCreated: addedAttachment[0].createdAt,
            dateModified: convertToTimeZone(new Date(), dateFormat.CONTENT_ATTACHMENT_DATE_FORMAT) || new Date(),
            items: [],
            hasChildren: addedAttachment[0].hasChildren,
            expanded: false,
            createdBy: user.id,
            icon: convertExtensionToIcon(addedAttachment[0]),
          };
          newYDocId = newYDoc.id;
          currentItem.items.push(newYDoc);
          if (!currentItem.hasChildren) currentItem.hasChildren = true;
        }
      }
      if (currentItem.items && currentItem.items.length) {
        const result = await addYDoc(currentItem.items, selectedItem, folderCount, projectId, customDoxName);
        currentItem.items = result.updatedData;
        if (result.newYDocId) newYDocId = result.newYDocId; // Retain the latest newYDocId
      }

      return currentItem;
    })
  );

  return { updatedData, newYDocId };
};

/**
 * Toggles the view button group state between grid and list views.
 *
 * @param {Object} btnGroupState - The current state of the button group.
 * @param {string} view - The desired view ("grid" or "list").
 * @returns {Object} The updated button group state.
 */
export const toggleViewBtnGroup = (btnGroupState, view) => {
  if (!btnGroupState.listView && view !== label.CMS_GRID) {
    return { gridView: false, listView: true };
  }
  if (!btnGroupState.gridView && view !== label.LIST) {
    return { gridView: true, listView: false };
  }
  return btnGroupState;
};

/**
 * Toggles the sort button group state between ascending and descending order.
 *
 * @param {Object} btnGroupState - The current state of the button group.
 * @param {string} curState - The current sort state ("asc" or "desc").
 * @returns {Object} The updated button group state.
 */
export const toggleSortBtnGroup = (btnGroupState, curState) => {
  if (!btnGroupState.sortDesc && curState !== "asc") {
    return { sortAsc: false, sortDesc: true };
  }
  if (!btnGroupState.sortAsc && curState !== "desc") {
    return { sortAsc: true, sortDesc: false };
  }
  return btnGroupState;
};

/**
 * Converts a file size in bytes to a human-readable format.
 *
 * @param {number} bytes - The size in bytes.
 * @param {number} [decimals=2] - The number of decimal places.
 * @returns {string} The formatted size string.
 */
export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

/**
 * Edits a specific data item by updating its path and modification date.
 *
 * @param {Array<Object>} data - The current data.
 * @param {Object} selectedItem - The item to edit.
 * @param {string} newPath - The new path for the selected item.
 * @returns {Array<Object>} The updated data.
 */
export const editDataItem = (data, selectedItem, updates, shouldUpdateItems = true) => {
  if (!data) return data;

  return data.map((item) => {
    const currentItem = { ...item };

    if (currentItem.id === selectedItem.id) {
      Object.assign(currentItem, updates, { dateModified: new Date() });
    }

    if (currentItem.items && shouldUpdateItems) {
      currentItem.items = editDataItem(currentItem.items, selectedItem, updates, shouldUpdateItems);
    }

    return currentItem;
  });
};

/**
 * Removes a specific data item from the tree structure.
 *
 * @param {Array<Object>} data - The current data.
 * @param {Object} selectedItem - The item to remove.
 * @returns {Array<Object>} The updated data with the item removed.
 */
export const removeDataItem = (data, selectedItem, field = "id") => {
  const invalidFolderForHasChildrenUpdate = ["recentRoot", "sharedWithMeRoot", "trashRoot"];
  if (!data || !selectedItem) return data;

  const newItems = [];

  for (let index = 0; index < data.length; index++) {
    const currentItem = { ...data[index] };
    const isMatch = field === "id" ? currentItem.id === selectedItem.id : currentItem.path === selectedItem.path;

    if (!isMatch) {
      if (currentItem.items && currentItem.items.length) {
        currentItem.items = removeDataItem(currentItem.items, selectedItem, field);
        if (!isValidSelectedFolder(currentItem, invalidFolderForHasChildrenUpdate))
          currentItem.hasChildren = currentItem.items.some((item) => item.type === label.FOLDER);
      }
      newItems.push(currentItem);
    }
  }
  return newItems;
};

/**
 * Converts a selected item's path into breadcrumb data.
 *
 * @param {Object|null} selectedItem - The selected item to convert.
 * @returns {Array<Object>} The breadcrumb data.
 */
export const convertToBreadcrumbData = (selectedItemPath, project) => {
  let path = [];

  if (!selectedItemPath) {
    return [
      {
        id: project,
        name: project,
      },
    ];
  }

  if (selectedItemPath) {
    const items = selectedItemPath.split("/");
    let curItemPath = [];

    for (let i = 0; i < items.length; i++) {
      curItemPath.push(items[i]);

      if (items[i] === project) {
        path.push({
          id: project,
          name: items[i],
        });
      } else {
        path.push({
          id: curItemPath.join("/"),
          name: items[i],
        });
      }
    }
  }
  return path;
};

/** Updates the tree data with fetched children
 * @author Muskan Thakur
 */
export const updateTreeData = (data, folderId, children = [], expanded = false, markFetched = false) => {
  return data.map((item) => {
    if (item.id === folderId) {
      return {
        ...item,
        expanded,
        isFetched: markFetched ? true : item.isFetched, // Update only if explicitly marked
        items: children.length >= 0 ? children : item.items || [],
      };
    }

    if (item.items && item.items.length > 0) {
      return {
        ...item,
        items: updateTreeData(item.items, folderId, children, expanded, markFetched),
      };
    }

    return item;
  });
};

/**
 *  Trim whitespace and problematic characters
 * @returns {sanitizeName}
 * @author Muskan Thakur
 */
export const sanitizeName = (name, isFile = false) => {
  let sanitized = name.trim().replace(/(&#39;|\/)/g, "");

  return sanitized || (getNotification(quote.NON_EMPTY_NAME, notifyIcon.WARNING_ICON), false);
};

/**splitter item details */
export const contentManagerSplitBtnItems = [
  { text: "Name", value: "path" },
  { text: "File Size", value: "size" },
  { text: "Date Created", value: "dateCreated" },
];

/**
 *The function returns `true` if the `selectedGridItem`'s ID is in the list of invalid IDs,
 * and `false` otherwise.
 * @param {Object} selectedGridItem
 */
export const isValidSelectedFolder = (selectedGridItem, invalidIds) => {
  if (!selectedGridItem?.id) return false;

  if (invalidIds.includes(selectedGridItem.id)) return true;

  return invalidIds.some((invalidId) => selectedGridItem.id.startsWith(`${invalidId}_`));
};

/**
 * An object representing the SVG icon for the "restore" action, including viewBox and path data.
 */
export const restoreSvgIcon = {
  viewBox: "0 0 24 24",
  content: `<path fill="currentColor" d="M7.11 8.53L5.7 7.11C4.8 8.27 4.24 9.61 4.07 11h2.02c.14-.87.49-1.72 1.02-2.47zM6.09 13H4.07c.17 1.39.72 2.73 1.62 3.89l1.41-1.42c-.52-.75-.87-1.59-1.01-2.47zm1.01 5.32c1.16.9 2.51 1.44 3.9 1.61V17.9c-.87-.15-1.71-.49-2.46-1.03L7.1 18.32zM13 4.07V1L8.45 5.55 13 10V6.09c2.84.48 5 2.94 5 5.91s-2.16 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93s-3.05-7.44-7-7.93z"/>`,
  name: "rotate_left",
};

export const doxSvgIcon = {
  viewBox: "0 0 512 512",
  fill: "none",
  content: `<path d='M352 32H96c-17.7 0-32 14.3-32 32v384c0 17.7 14.3 32 32 32h320c17.7 0 32-14.3 32-32V128zm64 416H96V64h224v96h96zm-32-96v32H128v-32zM256 160v32H128v-32m256 64v32H128v-32zm-256 96v-32h224v32z' fill='#4285F4'></path>`,
  name: "file-txt",
};

/**
 * handles downloading of a file
 * updates the last downloaded field for downloaded attachment
 * @author Muskan Thakur
 */
export const downloadAttachmentFile = async (attachment) => {
  try {
    await downloadFile(null, attachment.name, attachment.attachmentKey);
    await updateActionOnContentAttachment({
      attachmentId: attachment.id,
      type: "download",
    });
  } catch (error) {
    getNotification(label.DOWNLOAD_FAILED, notifyIcon.ERROR_ICON);
  }
};

/**
 * Handles the drop event for moving files or folders within a tree structure.
 */
export const handleDropHelper = ({ event, targetNode, dataTree, onFileDrop }) => {
  event.preventDefault();
  const raw = event.dataTransfer.getData("application/json");
  if (typeof raw !== "string" || raw.trim() === "") {
    return;
  }

  const droppedData = JSON.parse(raw);
  if (
    !targetNode ||
    targetNode.id === label.TASK_ASSETS_ROOT ||
    targetNode.id === label.RECENT_ROOT ||
    targetNode.id === label.TRASH_ROOT ||
    targetNode.id.startsWith(`${label.TASK_ASSETS_ROOT}_`) ||
    targetNode.id.startsWith(`${label.RECENT_ROOT}_`) ||
    targetNode.id.startsWith(`${label.TRASH_ROOT}_`) ||
    targetNode.type === label.FILE
  ) {
    getNotification(quote.INVALID_DROP, notifyIcon.WARNING_ICON);
    return;
  }

  const isAncestor = (node, targetId) => {
    if (!node.items) return false;
    if (node.items.some((child) => child.id === targetId)) return true;
    return node.items.some((child) => isAncestor(child, targetId));
  };

  for (const item of droppedData) {
    if (item.id === targetNode.id) {
      getNotification(quote.CANT_MOVE_FOLDER_INTO_SELF, notifyIcon.WARNING_ICON);
      return;
    }

    if (targetNode.items?.some((child) => child.id === item.id)) {
      getNotification(quote.ALREADY_PRESENT, notifyIcon.WARNING_ICON);
      return;
    }

    if (isAncestor(item, targetNode.id)) {
      getNotification(quote.CANT_MOVE_PARENT_TO_CHILD, notifyIcon.WARNING_ICON);
      return;
    }
  }

  const addChildrenToTree = (nodes) => {
    return nodes?.map((node) => {
      if (node.id === targetNode.id) {
        return {
          ...node,
          items: [
            ...(node.items || []),
            ...droppedData.map((item) => ({
              ...item,
              id: item.id,
              path: `${node.path}/${item.name}`,
              type: item.type,
              items: item.items || [],
            })),
          ],
        };
      } else if (node.items && node.items.length > 0) {
        return { ...node, items: addChildrenToTree(node.items) };
      }
      return node;
    });
  };

  const removeDataItems = (nodes, targetItems) => {
    return nodes
      ?.map((node) => {
        if (targetItems.some((item) => item.id === node.id)) {
          return null;
        } else if (node.items && node.items.length > 0) {
          return { ...node, items: removeDataItems(node.items, targetItems) };
        }
        return node;
      })
      .filter(Boolean);
  };

  const treeWithoutDroppedItems = removeDataItems(dataTree, droppedData);

  const updatedTreeData = addChildrenToTree(treeWithoutDroppedItems);

  if (onFileDrop) {
    onFileDrop(
      updatedTreeData,
      droppedData.map((item) => item.id),
      targetNode.id
    );
  }
};

/**
 * Fetches folder content based on the folderId.
 */
export const useFolderHelpers = () => {
  const dispatch = useDispatch();
  const state = store.getState();
  const { user } = state.auth;
  const isDataFetching = useRef(new Map());

  const fetchFolderContent = async (folderItem, userId, projectId) => {
    const { id: folderId } = folderItem;
    const resolvedProjectId = projectId ?? folderItem?.projectId ?? null;

    if (folderId === label.TRASH_ROOT || folderId.startsWith(`${label.TRASH_ROOT}_`)) {
      const extractedProjectId = folderId.startsWith(`${label.TRASH_ROOT}_`) ? folderId.split("_")[1] : null;
      return await getDeletedContentAttachments({
        projectId: extractedProjectId || resolvedProjectId,
        userId,
      });
    } else if (folderId === label.RECENT_ROOT || folderId.startsWith(`${label.RECENT_ROOT}_`)) {
      const extractedProjectId = folderId.startsWith(`${label.RECENT_ROOT}_`) ? folderId.split("_")[1] : null;
      return await getRecentContentAttachments({
        projectId: extractedProjectId || resolvedProjectId,
        userId,
      });
    } else if (folderId === label.QUICK_ACCESS_ROOT) {
      return await getContentAttachments({ parentId: folderItem.quickAccessFolderId ?? null, projectId: projectId ?? null, userId });
    } else if (
      folderId === label.TASK_ASSETS_ROOT ||
      folderId.startsWith(`${label.TASK_ASSETS_ROOT}_`) ||
      folderItem?.parentId === label.TASK_ASSETS_ROOT
    ) {
      const extractedProjectId = folderId.startsWith(`${label.TASK_ASSETS_ROOT}_`) ? folderId.split("_")[1] : null;
      return await getTaskContentAttachments({
        parentId: folderId === label.TASK_ASSETS_ROOT || folderId.startsWith(`${label.TASK_ASSETS_ROOT}_`) ? null : folderId,
        projectId: extractedProjectId || resolvedProjectId,
        userId,
      });
    } else {
      let actualParentId = folderId;
      let actualProjectId = resolvedProjectId;

      if (folderId === label.PROJECT_ROOT) {
        actualParentId = null;
      } else if (folderId.startsWith(`${label.PROJECT_ROOT}_`) || folderId.startsWith("project_")) {
        const extractedProjectId = folderId.includes("_") ? folderId.split("_")[1] : null;
        actualProjectId = extractedProjectId || projectId;
        actualParentId = null;
      }

      return await getContentAttachments({
        parentId: actualParentId,
        projectId: actualProjectId,
        userId,
      });
    }
  };

  const fetchAndSetFolder = async (folderId, folderItem, projectId, forceFetch = false) => {
    if (!forceFetch && (folderItem?.isFetched || isDataFetching.current.get(folderId))) return;

    isDataFetching.current.set(folderId, true);
    try {
      const fetchedChildren = await fetchFolderContent(folderItem, user.id, projectId);
      const updatedState = store.getState();
      const updatedPath = updatedState.contentManager.contentManagerLibraryPath;
      const updatedPathContentManagerData = updatedState.contentManager.contentManagerData;

      const updatedData = updateTreeData(updatedPathContentManagerData, folderId, fetchedChildren?.data, true, true);
      const formattedData = formatData(updatedData, updatedPath);
      const updatedSelectedTreeItem = searchTreeItem(formattedData, { id: folderItem?.id });

      dispatch(setSelectedContentItem(updatedSelectedTreeItem));
      dispatch(setContentManagerData(formattedData));
    } finally {
      isDataFetching.current.delete(folderId);
    }
  };

  return { fetchAndSetFolder };
};

/**Invalid folder ids for the given action */
export const invalidFolderIdsForAction = [
  label.PROJECT_ROOT,
  label.TASK_ASSETS_ROOT,
  label.RECENT_ROOT,
  label.SHARED_ROOT,
  label.TRASH_ROOT,
  label.DOCUMENTS_ROOT,
  label.QUICK_ACCESS_ROOT,
];

/**
 * Restores missing parent items in the hierarchy.
 */
export const restoreMissingParents = (missingParents, restoredData, libraryName) => {
  missingParents?.forEach((parent) => {
    let parentOfParent = searchTreeItem(restoredData, parent.parentId ? { id: parent.parentId } : { path: libraryName });
    const mappedParent = mapData([parent], parentOfParent?.path)[0];

    if (parentOfParent) {
      if (!parentOfParent.items) parentOfParent.items = [];
      const isDuplicate = parentOfParent.items.some((item) => item.id === mappedParent.id);
      if (!isDuplicate) {
        parentOfParent.items.push(mappedParent);
        parentOfParent.hasChildren = !!parentOfParent.items?.some((item) => item.type === label.FOLDER);
        parentOfParent.expanded = true;
        parentOfParent.isDeleted = false;
        restoredData[0].expanded = true;
      }
    }
  });
};

/**
 * Updates the application state and fetches folder data after restoring an item.
 */
export const updateStateAndFetchData = async (mappedRestoredItem, restoredData, projectId, fetchAndSetFolder, showItem = false) => {
  const state = store.getState();
  const { contentManagerLibraryPath } = state.contentManager;
  const parentItem = searchTreeItem(restoredData, { id: mappedRestoredItem?.parentId });
  if (parentItem) {
    parentItem.isDeleted = false;
    showItem ? store.dispatch(setContentManagerDataDetails(mappedRestoredItem)) : store.dispatch(setContentManagerDataDetails(parentItem));
    store.dispatch(setSelectedContentItem(parentItem));
    store.dispatch(setContentManagerData(restoredData));
    if (!parentItem.item) {
      await fetchAndSetFolder(parentItem.id, parentItem, projectId, false);
    }
  }
};

/**
 * Updates the path of the restored item if necessary.
 */
const updateRestoredItemPath = (mappedRestoredItem) => {
  let oldPath = mappedRestoredItem.path.split("/");
  oldPath.pop();
  if (mappedRestoredItem.type === label.FILE) {
    mappedRestoredItem.path = oldPath.join("/") === label.PROJECT_ROOT ? props.libraryName : oldPath.join("/");
  }
};

/**
 * Restores an item and attaches it to its correct parent in the hierarchy.
 */
export const restoreItemToParent = ({ restoredItem, restoredData, libraryName, projectId, fetchAndSetFolder, showItem = false }) => {
  let restoredParent = searchTreeItem(restoredData, restoredItem.parentId ? { id: restoredItem.parentId } : { path: libraryName });

  let mappedRestoredItem = mapData([restoredItem], restoredParent?.path)[0];

  let parentItem = searchTreeItem(restoredData, { path: mappedRestoredItem.parentPath });
  if (parentItem) {
    if (!parentItem.items) parentItem.items = [];
    const isDuplicate = parentItem.items.some((item) => item.id === mappedRestoredItem.id);
    if (!isDuplicate) {
      parentItem.items.push(mappedRestoredItem);
      parentItem.hasChildren = !!parentItem.items?.some((item) => item.type === label.FOLDER);
      parentItem.expanded = true;
      restoredData[0].expanded = true;
    }
  } else {
    let libraryFolder = searchTreeItem(restoredData, { path: libraryName });
    updateStateAndFetchData(libraryFolder, restoredData, projectId, fetchAndSetFolder, showItem);
    return;
  }

  updateRestoredItemPath(mappedRestoredItem);
  updateStateAndFetchData(mappedRestoredItem, restoredData, projectId, fetchAndSetFolder, showItem);
};

/**generates path for respective item from parents */
export const generateCmsAttachmentPath = (folders, targetFolder, libraryName) => {
  const folderMap = new Map(folders.map((folder) => [folder._id, folder]));

  const buildPath = (folderId) => {
    const folder = folderMap.get(folderId);
    if (!folder) return "";
    return folder.parentId ? buildPath(folder.parentId) + "/" + folder.name : folder.name;
  };

  return targetFolder.parentId !== label.PROJECT_ROOT && targetFolder.parentId !== null
    ? `${libraryName}/${buildPath(targetFolder.parentId)}`
    : `${libraryName}`;
};

/**handles the path formation when the item is selected */
export const handlePath = async (selectedItem, libraryName, setSelectedItemPath, setMissingParents) => {
  const response = await getContentAttachmentById(selectedItem.id);
  let { missingParents } = response;
  setSelectedItemPath(generateCmsAttachmentPath(missingParents, selectedItem, libraryName));
  setMissingParents(missingParents);
};

/**
 * most operations occur in the library folder, so CMS data is filtered to exclude items from other folders.
 */
export const resetContentManagerItems = (indicesToReset = [number.TWO, number.FOUR]) => {
  const state = store.getState();
  const { contentManagerData } = state.contentManager;
  const clonedData = [...contentManagerData];

  if (clonedData.length > Math.max(...indicesToReset)) {
    indicesToReset.forEach((index) => {
      clonedData[index] = { ...clonedData[index], items: [] };
    });
  }

  return clonedData;
};

/**Handles updating content attachment data and synchronizing it with the state. */
export const handleContentUpdate = async (updatedFields, projectId) => {
  const state = store.getState();
  const { contentManagerDetailsData, contentManagerData, selectedContentManagerItem } = state.contentManager;

  let actualProjectId = projectId;
  if (selectedContentManagerItem?.id?.startsWith(`${label.PROJECT_ROOT}_`)) {
    const extractedProjectId = selectedContentManagerItem.id.split("_")[1];
    actualProjectId = extractedProjectId ? parseInt(extractedProjectId) : projectId;
  }

  await updateContentAttachment({
    attachmentId: contentManagerDetailsData?.id,
    ...updatedFields,
    projectId: actualProjectId ?? null,
  });
  const updatedData = editDataItem(contentManagerData, contentManagerDetailsData, updatedFields);
  store.dispatch(
    setSelectedContentItem(
      searchTreeItem(updatedData, {
        id: contentManagerDetailsData?.type === label.FILE ? contentManagerDetailsData?.parentId : contentManagerDetailsData?.id,
      })
    )
  );
  store.dispatch(setContentManagerDataDetails(searchTreeItem(updatedData, { id: contentManagerDetailsData?.id })));
  store.dispatch(setContentManagerData(updatedData));
};

/**
 * Handles the reminder for a library attachment by optionally navigating to the appropriate library view.
 * @author Muskan Thakur
 */
export const handleLibraryAttachmentClick = async (attachmentId, history, fetchAndSetFolder) => {
  const state = store.getState();
  const { contentManagerLibraryPath } = state.contentManager;
  const { myProjectId } = state.auth.user;
  const { allProjectsList } = state.tasks.defaultDetails;
  const attachmentData = await getContentAttachmentById(attachmentId);
  const { item } = attachmentData;
  const attachmentProjectId = item?.projectId === myProjectId ? null : item?.projectId;

  let pathname = location.pathname;
  const { params } = matchPath(pathname, {
    path: [route.PRIVATE_ROUTE.CMS.ROUTER_PATH],
  }) || { params: {} };
  let projectId = params?.projectId || null;

  let project = null;

  if (attachmentProjectId) {
    project = allProjectsList?.find((proj) => proj?.ProjectId === attachmentProjectId);
  }

  const ProjectName = project?.ProjectName || "";

  const libraryName = `${contentManagerLibraryPath}/${
    item?.workflowId
      ? attachmentProjectId
        ? `${ProjectName} ${label.TASK_ASSETS}`
        : label.TASK_ASSETS
      : attachmentProjectId
      ? `${label.LIBRARY}`
      : label.MY_LIBRARY
  }`;

  const [mappedRestoredItem] = mapData([item], libraryName);
  let restoredData = resetContentManagerItems();

  store.dispatch(
    setLibraryAttachment({
      ...attachmentData,
      item: mappedRestoredItem,
    })
  );

  if (pathname.includes(route.PRIVATE_ROUTE.CMS.PATH) && attachmentProjectId == projectId) {
    const newItem = searchTreeItem(restoredData, { id: mappedRestoredItem.id });
    if (newItem) {
      let itemToSelect;

      if (newItem?.type === label.FILE) {
        const newItemParent = searchTreeItem(restoredData, { id: mappedRestoredItem.parentId });
        itemToSelect = newItemParent;
      } else {
        itemToSelect = newItem;
      }
      store.dispatch(setSelectedContentItem(itemToSelect));
      store.dispatch(setContentManagerDataDetails(newItem));
    } else {
      const missingParents = attachmentData?.missingParents;
      const reversedMissingParents = missingParents ? [...missingParents].reverse() : [];
      restoreMissingParents(reversedMissingParents, restoredData, libraryName);
      const payload = {
        restoredItem: mappedRestoredItem,
        restoredData,
        libraryName,
        projectId: attachmentProjectId,
        fetchAndSetFolder,
        showItem: true,
      };
      restoreItemToParent(payload);
    }
    store.dispatch(setContentManagerDataDetails(mappedRestoredItem));
  } else {
    resetContentManagerState();
    history.push(attachmentProjectId ? `${route.PRIVATE_ROUTE.CMS.PATH}/${attachmentProjectId}/${ProjectName}` : route.PRIVATE_ROUTE.CMS.PATH);
  }
};
/**
 * Resets the state of the content manager
 */
export const resetContentManagerState = () => {
  store.dispatch(setSelectedContentItem([]));
  store.dispatch(setContentManagerDataDetails([]));
  store.dispatch(setFileMetaData({}));
  store.dispatch(setContentManagerDataView(label.CMS_GRID));
  store.dispatch(setContentManagerDataDetails([]));
};

/**gets required name for the given field */
export const getSortField = (field) => {
  switch (field) {
    case "path":
      return "name";
    case "dateCreated":
      return "date";
    default:
      return field;
  }
};

/**fetches sorted CMS data */
export const fetchAndSetSortedContentManagerItems = async ({ parentId, page, sortBy, sortOrder, isTaskAssets = false, isQuickAccess = false, projectId }) => {
  const state = store.getState();
  const { contentManagerData, selectedContentManagerItem, contentManagerLibraryPath } = state.contentManager;
  const { user } = state.auth;
  let response;
  if (selectedContentManagerItem?.id === label.TRASH_ROOT) {
    response = await getDeletedContentAttachments({
      projectId: projectId ?? null,
      userId: user?.id,
      sortBy,
      sortOrder,
    });
  } else if (selectedContentManagerItem?.id === label.RECENT_ROOT) {
    response = await getRecentContentAttachments({ projectId: projectId ?? null, userId: user?.id });
  } else if (isTaskAssets || selectedContentManagerItem?.parentId === label.TASK_ASSETS_ROOT) {
    response = await getTaskContentAttachments({
      parentId,
      projectId: projectId ?? null,
      userId: user?.id,
      page,
      sortBy,
      sortOrder,
    });
  } else {
    response = await getContentAttachments({
      parentId,
      projectId: projectId ?? null,
      userId: user?.id,
      page,
      sortBy,
      sortOrder,
    });
  }

  const formattedChildrenData = formatData(response?.data, contentManagerLibraryPath);

  const updatedData = updateTreeData(
    contentManagerData,
    selectedContentManagerItem?.id,
    [...(selectedContentManagerItem?.items ?? []), ...formattedChildrenData],
    true,
    true
  );

  const formattedData = formatData(updatedData, contentManagerLibraryPath);
  const updatedSelectedTreeItem = searchTreeItem(formattedData, { id: selectedContentManagerItem?.id });

  store.dispatch(setSelectedContentItem(updatedSelectedTreeItem));
  store.dispatch(setContentManagerData(formattedData));

  return {
    nextPage: response?.pagination?.page,
    isLastAttachment: !response?.data?.length,
  };
};

/** Common sorting logic for content manager */
export const handleContentManagerSort = async ({ sort, selectedContentManagerItem, projectId, userId, libraryName }) => {
  if (!sort[0]) return null;

  const state = store.getState();
  const { user } = state.auth;

  const { field, dir } = sort[0];

  let actualProjectId = projectId;
  if (selectedContentManagerItem?.id.startsWith(`${label.PROJECT_ROOT}_`)) {
    const extractedProjectId = selectedContentManagerItem.id.split("_")[1];
    actualProjectId = extractedProjectId ? parseInt(extractedProjectId) : projectId;
  } else if (selectedContentManagerItem?.id.startsWith(`${label.TASK_ASSETS_ROOT}_`)) {
    const extractedProjectId = selectedContentManagerItem.id.split("_")[1];
    actualProjectId = extractedProjectId ? parseInt(extractedProjectId) : projectId;
  }

  const parentId =
    selectedContentManagerItem?.id === label.PROJECT_ROOT ||
    selectedContentManagerItem?.id === label.TASK_ASSETS_ROOT ||
    selectedContentManagerItem?.id === label.QUICK_ACCESS_ROOT ||
    selectedContentManagerItem?.id.startsWith(`${label.PROJECT_ROOT}_`) ||
    selectedContentManagerItem?.id.startsWith(`${label.TASK_ASSETS_ROOT}_`)
      ? null
      : selectedContentManagerItem?.id;
  const sortBy = getSortField(field);
  const sortOrder = dir === "asc" ? number.ONE : -number.ONE;

  let response;
  if (selectedContentManagerItem?.id === label.TRASH_ROOT) {
    response = await getDeletedContentAttachments({
      projectId: actualProjectId ?? null,
      userId: user?.id,
      sortBy,
      sortOrder,
    });
  } else if (selectedContentManagerItem?.id === label.RECENT_ROOT) {
    response = await getRecentContentAttachments({ projectId: actualProjectId ?? null, userId: user?.id });
  } else if (selectedContentManagerItem?.parentId === label.TASK_ASSETS_ROOT) {
    const payload = { parentId, projectId: actualProjectId ?? null, userId: user?.id, page: number.ONE, sortBy, sortOrder };
    response = await getTaskContentAttachments(payload);
  } else {
    const payload = { parentId, projectId: actualProjectId ?? null, userId: user?.id, page: number.ONE, sortBy, sortOrder };
    response = await getContentAttachments(payload);
  }
  return {
    formattedData: formatData(response?.data, libraryName),
    response,
  };
};

/**
 * marks the selected item as favorite
 */
export const handleMarkAsFavorite = async (selectedGridItem, projectId) => {
  const state = store.getState();
  const { contentManagerData, selectedContentManagerItem } = state.contentManager;
  const { user } = state.auth;
  const folderPath = getFolderPath(selectedGridItem.path);
  await updateContentAttachment({
    attachmentId: selectedGridItem?.id,
    isFavorite: !selectedGridItem?.isFavorite,
    projectId: selectedContentManagerItem?.id?.startsWith(`${label.PROJECT_ROOT}_`)
      ? parseInt(selectedContentManagerItem.id.split("_")[1])
      : projectId ?? null,
  });
  const updatedData = editDataItem(contentManagerData, selectedGridItem, { isFavorite: !selectedGridItem?.isFavorite });
  store.dispatch(setSelectedContentItem(searchTreeItem(updatedData, { path: folderPath })));
  store.dispatch(setContentManagerDataDetails(searchTreeItem(updatedData, { path: selectedGridItem.path })));
  store.dispatch(setContentManagerData(updatedData));
  store.dispatch(getBookmarks(user.id));
};
